import {Group, Layer, Line, Text} from "react-konva";
import CanvasImage from "@/components/konva/CanvasImage.tsx";
import type {Image} from "@/images/Image.ts";
import type {GridPoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {gridToImage} from "@/dungeons/geometry/Coordinates.ts";
import DungeonGridShape from "@/dungeons/geometry/DungeonGridShape.tsx";
import {doorAppearance} from "@/dungeons/editor/geometry/DoorAppearance.ts";
import type {SelectedGeometryFeature} from "@/dungeons/editor/geometry/GeometryEditorTypes.ts";
import type {ReactNode} from "react";

type CanvasTransform = { x: number; y: number; scaleX: number; scaleY: number };

export default function DungeonMapLayers({image, geometry, transform, zoom, hiddenFeature, showAnnotations = true, lighting}: {
    image: Image;
    geometry: LevelGeometryDocument;
    transform: CanvasTransform;
    zoom: number;
    hiddenFeature?: SelectedGeometryFeature;
    showAnnotations?: boolean;
    lighting?: ReactNode;
}) {
    return <>
        <Layer listening={false}><Group {...transform}>
            <CanvasImage image={image} width={image.width} height={image.height}/>
        </Group></Layer>
        {lighting}
        <Layer listening={false}><Group {...transform}>
            <DungeonGridShape width={image.width} height={image.height}
                              registration={geometry.registration} zoom={zoom}/>
        </Group></Layer>
        <Layer listening={false}><Group {...transform}>
            <GeometryPreview geometry={geometry} zoom={zoom} hiddenFeature={hiddenFeature}
                             showAnnotations={showAnnotations}/>
        </Group></Layer>
    </>;
}

function GeometryPreview({geometry, zoom, hiddenFeature, showAnnotations}: {
    geometry: LevelGeometryDocument;
    zoom: number;
    hiddenFeature?: SelectedGeometryFeature;
    showAnnotations: boolean;
}) {
    const toImage = (point: GridPoint) => gridToImage(point, geometry.registration);
    return <>
        {geometry.walls
            .filter(wall => hiddenFeature?.type !== "wall" || wall.id !== hiddenFeature.id)
            .map(wall => <Line
                key={wall.id}
                points={wall.points.flatMap(point => {
                    const image = toImage(point);
                    return [image.x, image.y];
                })}
                stroke="#ef4444"
                strokeWidth={3 / zoom}
                lineCap="round" lineJoin="round"/>)}
        {geometry.doors.filter(door => hiddenFeature?.type !== "door" || door.id !== hiddenFeature.id).map(door => {
            const start = toImage(door.start);
            const end = toImage(door.end);
            const appearance = doorAppearance(door, zoom);
            return <Line key={door.id}
                         points={[start.x, start.y, end.x, end.y]} stroke={appearance.stroke}
                         strokeWidth={5 / zoom} dash={appearance.dash} lineCap="round"/>;
        })}
        {geometry.transitions.filter(transition => hiddenFeature?.type !== "transition" ||
            transition.id !== hiddenFeature.id).map(transition =>
            <Line key={transition.id} points={transition.points.flatMap(point => {
                const image = toImage(point);
                return [image.x, image.y];
            })} closed fill="rgba(34,197,94,0.25)" stroke="#22c55e"
                  strokeWidth={3 / zoom} lineJoin="round"/>)}
        {showAnnotations && geometry.annotations.map(annotation => {
            const point = toImage(annotation.position);
            return <Text key={annotation.id}
                         x={point.x + 8 / zoom} y={point.y - 8 / zoom} text={annotation.label || annotation.title}
                         fontSize={14 / zoom} fill="#fde047"/>;
        })}
    </>;
}
